import apiClient, { type ApiError } from './apiClient'
import { isAuthError, createAuthError, extractErrorMessage } from '@/lib/utils/errorHandling'
import { extractArrayFromResponse } from './utils/responseParser'
import { logDebug, logWarning, logError } from './utils/logger'
import { API_ENDPOINTS } from './config'

/**
 * Street type from API response
 */
export interface StreetTypeResponse {
  street_suffix: string
  street_suffix_code: string
  id?: number
}

/**
 * Street type interface for application use
 */
export interface StreetType {
  id: number
  street_suffix: string
  street_suffix_code: string
  // Legacy support for backward compatibility
  name: string
  abbreviation: string
}

/**
 * Get street types from API
 * @returns Array of street types
 * @throws ApiError if request fails
 */
export async function getStreetTypes(): Promise<StreetType[]> {
  try {
    const response = await apiClient.get<StreetTypeResponse[] | unknown>(API_ENDPOINTS.STREETS)
    const responseData = response.data

    logDebug('Streets API Response:', responseData)

    // Extract array from various response formats
    const rawTypes = extractArrayFromResponse<StreetTypeResponse>(responseData)

    if (rawTypes.length === 0) {
      logWarning('No street types returned from API')
      return []
    }

    // Map API response to application format
    const mappedTypes: StreetType[] = rawTypes.map((item, index) => {
      // Validate required fields
      if (!item || typeof item !== 'object') {
        logWarning('Invalid street type item at index', index, item)
        return createFallbackStreetType(index)
      }

      const apiItem = item as StreetTypeResponse

      // Primary format: { street_suffix, street_suffix_code }
      if (apiItem.street_suffix && apiItem.street_suffix_code) {
        return {
          id: apiItem.id ?? index,
          street_suffix: apiItem.street_suffix,
          street_suffix_code: apiItem.street_suffix_code,
          name: apiItem.street_suffix,
          abbreviation: apiItem.street_suffix_code,
        }
      }

      // Legacy format support
      const legacyItem = item as { name?: string; abbreviation?: string; id?: number }
      if (legacyItem.name) {
        return {
          id: legacyItem.id ?? index,
          street_suffix: legacyItem.name,
          street_suffix_code: legacyItem.abbreviation || legacyItem.name,
          name: legacyItem.name,
          abbreviation: legacyItem.abbreviation || legacyItem.name,
        }
      }

      // Fallback for invalid data
      logWarning('Invalid street type format at index', index, item)
      return createFallbackStreetType(index)
    })

    logDebug(`Mapped ${mappedTypes.length} street types`)
    return mappedTypes
  } catch (error) {
    logError('Failed to fetch street types:', error)

    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to fetch street types.'),
      status: (error as ApiError).status || 500,
    } as ApiError
  }
}

let streetTypesRequest: Promise<StreetType[]> | null = null

/**
 * Deduplicates concurrent calls to {@link getStreetTypes} (one in-flight request shared by all callers).
 */
export function getStreetTypesCached(): Promise<StreetType[]> {
  if (!streetTypesRequest) {
    streetTypesRequest = getStreetTypes().catch((err) => {
      streetTypesRequest = null
      throw err
    })
  }
  return streetTypesRequest
}

/**
 * Create a fallback street type for invalid data
 */
function createFallbackStreetType(index: number): StreetType {
  return {
    id: index,
    street_suffix: '',
    street_suffix_code: '',
    name: '',
    abbreviation: '',
  }
}


